import React, { useState, useEffect } from 'react';
import FlareMultiSlider from '../FlareMultiSlider';
import useFlareStore from '../../stores/useFlareStore';
import NumberInput from '../ui/NumberInput';
import DynamicSlider from '../ui/DynamicSlider';
import styles from './ToolCard.module.css';
import { TOOLS_CONFIG } from '../../constants/tools';
import useEditorStore from '../../stores/useEditorStore';
import { useToolManager } from '../../hooks/useToolManager';

const FlareToolCard = () => {
    const setHeightValue = useFlareStore((state) => state.setHeightValue);
    const setDeflectValue = useFlareStore((state) => state.setDeflectValue);
    const setFalloffHeightValue = useFlareStore((state) => state.setFalloffHeightValue);
    const setFalloffDeflectValue = useFlareStore((state) => state.setFalloffDeflectValue);
    const getSelectedHeightValue = useFlareStore((state) => state.getSelectedHeightValue);
    const getSelectedDeflectValue = useFlareStore((state) => state.getSelectedDeflectValue);
    const getSelectedFalloffHeightValue = useFlareStore((state) => state.getSelectedFalloffHeightValue);
    const getSelectedFalloffDeflectValue = useFlareStore((state) => state.getSelectedFalloffDeflectValue);
    const getSelectedSegmentName = useFlareStore((state) => state.getSelectedSegmentName);
    const getSegmentPercentages = useFlareStore((state) => state.getSegmentPercentages);
    const selectedSegmentIndex = useFlareStore((state) => state.selectedSegmentIndex);
    const flareValues = useFlareStore((state) => state.flareValues);
    const resetAllValues = useFlareStore((state) => state.resetAllValues);
    const deflectBlendRatio = useFlareStore((state) => state.deflectBlendRatio);
    const setDeflectBlendRatio = useFlareStore((state) => state.setDeflectBlendRatio);

    const isFlareApplied = useFlareStore((state) => state.isFlareApplied);
    const undoCommand = useFlareStore((state) => state.currentFlareCommand);
    const isAppliedFromDraft = useFlareStore((state) => state.isFlareAppliedFromDraft);

    const { run } = useToolManager();

    const [heightValue, setHeightValueLocal] = useState(getSelectedHeightValue());
    const [deflectValue, setDeflectValueLocal] = useState(getSelectedDeflectValue());
    const [falloffHeightValue, setFalloffHeightValueLocal] = useState(getSelectedFalloffHeightValue());
    const [falloffDeflectValue, setFalloffDeflectValueLocal] = useState(getSelectedFalloffDeflectValue());
    const [blendRatioValue, setBlendRatioValueLocal] = useState(deflectBlendRatio);

    //Controls Values
    const MIN_HEIGHT = 0;
    const MAX_HEIGHT = 1000;
    const MIN_DEFLECT = -1000;
    const MAX_DEFLECT = 1000;
    const STEP_SIZE = 1;

    // Sync with store when selected segment changes
    useEffect(() => {
        setHeightValueLocal(getSelectedHeightValue());
        setDeflectValueLocal(getSelectedDeflectValue());
        setFalloffHeightValueLocal(getSelectedFalloffHeightValue());
        setFalloffDeflectValueLocal(getSelectedFalloffDeflectValue());
    }, [selectedSegmentIndex, getSelectedHeightValue, getSelectedDeflectValue, getSelectedFalloffHeightValue, getSelectedFalloffDeflectValue]);

    // Sync blend ratio with store
    useEffect(() => {
        setBlendRatioValueLocal(deflectBlendRatio);
    }, [deflectBlendRatio]);

    const handleHeightChange = (value) => {
        const numValue = value ?? 0;
        setHeightValueLocal(numValue);
        setHeightValue(numValue);
    };

    const handleDeflectChange = (value) => {
        const numValue = value ?? 0;
        setDeflectValueLocal(numValue);
        setDeflectValue(numValue);
    };

    const handleFalloffHeightChange = (value) => {
        const numValue = value ?? 0;
        setFalloffHeightValueLocal(numValue);
        setFalloffHeightValue(numValue);
    };

    const handleFalloffDeflectChange = (value) => {
        const numValue = value ?? 0;
        setFalloffDeflectValueLocal(numValue);
        setFalloffDeflectValue(numValue);
    };

    const applyFlare = () => {
        if (!isFlareApplied) {
            run({ tool: TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_flare.id, mode: 'apply' });
        } else if (undoCommand) {
            const { resetHistoryUpTo } = useEditorStore.getState();
            resetHistoryUpTo(undoCommand);
            undoCommand.undo();
        }
    };

    const handleReset = () => {
        resetAllValues();
        // Update local state to reflect the reset
        setHeightValueLocal(0);
        setDeflectValueLocal(0);
        setFalloffHeightValueLocal(0);
        setFalloffDeflectValueLocal(0);
    };

    const isApplied = isFlareApplied;
    const isLoadedFromDraft = isApplied && isAppliedFromDraft;

    return (
        <div className='flex flex-col absolute left-[10px] top-[10px]'>
            <div className='bg-white rounded-md p-2 pb-3 shadow-[0_2px_8px_rgba(0,0,0,0.15)] w-[264px] z-[1000] max-h-[70vh] overflow-y-auto border border-gray-200'>
                <div className={styles.toolCardHeader}>
                    <h3 style={{marginBottom: '0px'}}>Flare Dial</h3>
                    {!isFlareApplied && <p>Configure flare settings</p>}
                    {isFlareApplied && !isLoadedFromDraft && <p>☑️ Flare Applied</p>}
                    {isFlareApplied && isLoadedFromDraft && <p>☑️ Flare Applied from Draft</p>}
                </div>

                {!isFlareApplied && (
                <div className='mb-4'>

                {/* Flare Multi Slider */}
                <div className='bg-slate-50 rounded-lg p-4 mb-4 border border-slate-200 mx-2'>
                    <FlareMultiSlider
                        width={240}
                        height={240}
                        radius={80}
                        strokeWidth={20}
                        showLabels={false}
                        enableEdit={true}
                    />
                </div>

                <div className='rounded-lg bg-white mb-4 mx-2'>
                    {/* Selected Color Indicator */}
                    <div className='flex items-center justify-between gap-2 mb-4 p-2 bg-slate-50 rounded-md border border-slate-200'>
                        <div className='flex items-center gap-2'>
                            <div style={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: (() => {
                                    const colorMap = {
                                        purple: '#B984D9',
                                        yellow: '#FFD152',
                                        orange: '#F19B4A',
                                        blue: '#3B9FF5',
                                        violet: '#9093DF'
                                    };
                                    return colorMap[getSelectedSegmentName()] || '#B984D9';
                                })(),
                                border: '2px solid white',
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                            }} />
                            <span style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: '#374151',
                                textTransform: 'capitalize'
                            }}>
                                {getSelectedSegmentName()} Selected
                            </span>
                        </div>
                        <span style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#7c3aed',
                            padding: '2px 8px',
                            backgroundColor: '#f3e8ff',
                            borderRadius: 6
                        }}>
                            {getSegmentPercentages()[selectedSegmentIndex].toFixed(1)}%
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Height and Deflect Group */}
                        <div style={{
                            padding: 12,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 8,
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#6b7280',
                                marginBottom: 12,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Main Values
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ height: 40 }}>
                                    <NumberInput
                                        value={heightValue}
                                        onChange={handleHeightChange}
                                        min={MIN_HEIGHT}
                                        max={MAX_HEIGHT}
                                        step={STEP_SIZE}
                                        label="Height"
                                        unit="mm"
                                    />
                                </div>

                                <div style={{ height: 40 }}>
                                    <NumberInput
                                        value={deflectValue}
                                        onChange={handleDeflectChange}
                                        min={MIN_DEFLECT}
                                        max={MAX_DEFLECT}
                                        step={STEP_SIZE}
                                        label="Deflect"
                                        unit="mm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Falloff Group */}
                        <div style={{
                            padding: 12,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 8,
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#6b7280',
                                marginBottom: 12,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Secondard Values
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ height: 40 }}>
                                    <NumberInput
                                        value={falloffHeightValue}
                                        onChange={handleFalloffHeightChange}
                                        min={MIN_HEIGHT}
                                        max={MAX_HEIGHT}
                                        step={STEP_SIZE}
                                        label="Height"
                                        unit="mm"
                                    />
                                </div>

                                <div style={{ height: 40 }}>
                                    <NumberInput
                                        value={falloffDeflectValue}
                                        onChange={handleFalloffDeflectChange}
                                        min={MIN_DEFLECT}
                                        max={MAX_DEFLECT}
                                        step={STEP_SIZE}
                                        label="Deflect"
                                        unit="mm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Blend Ratio Control */}
                        <div style={{
                            padding: 12,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 8,
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#6b7280',
                                marginBottom: 8,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Vertical Blend
                            </div>

                            <DynamicSlider
                                min={0.1}
                                max={1}
                                step={0.05}
                                value={blendRatioValue}
                                onChange={(value) => {
                                    setBlendRatioValueLocal(value);
                                    setDeflectBlendRatio(value);
                                }}
                                label='Ratio'
                                layout='inline'
                                valueClassName='bg-gray-200'
                            />
                        </div>
                    </div>
                </div>
                </div>
                )}

                {/* Footer inside card */}
                {isFlareApplied && (
                <div className={styles.toolCardFooter}>
                    <button
                        className={`px-2 py-1 rounded-md min-w-20 text-white ${
                            isLoadedFromDraft
                                ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
                        }`}
                        onClick={isLoadedFromDraft ? undefined : applyFlare}
                        disabled={isLoadedFromDraft}
                        title={isLoadedFromDraft ? "Cannot unapply flare loaded from draft" : "Unapply flare"}
                    >
                        Unapply
                    </button>
                </div>
                )}
            </div>

            {/* Apply button area - outside card, only when NOT applied */}
            {!isFlareApplied && (
                <div className='flex items-center justify-between mt-4 px-2'>
                    <button
                        className='px-3 py-1 rounded-md min-w-20 text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 cursor-pointer transition-colors'
                        onClick={handleReset}
                        title="Reset all values to 0"
                    >
                        Reset
                    </button>
                    <button
                        className={`px-2 py-1 rounded-md ml-2 min-w-22 text-white text-lg cursor-pointer bg-violet-600 hover:text-white hover:bg-violet-500`}
                        onClick={applyFlare}
                    >
                        Apply
                    </button>
                </div>
            )}
        </div>
    );
};

export default FlareToolCard;